import { useCallback, useEffect, useRef, useState } from 'react'

import CompanyBackground from '../../../../../assets/images/CompanyModalBackground.jpg'
import Bag2Icon from '../../../../../assets/svgs/Bag2'
import ChartsIcon from '../../../../../assets/svgs/Charts'
import CrossSpotlightIcon from '../../../../../assets/svgs/CrossSpotlight'
import HatIcon from '../../../../../assets/svgs/Hat'
import InfoLight from '../../../../../assets/svgs/InfoLight'
import Location2Icon from '../../../../../assets/svgs/Location2'
import NotesIcon from '../../../../../assets/svgs/Notes'
import PenIcon from '../../../../../assets/svgs/Pen'
import PeopleIcon from '../../../../../assets/svgs/People'
import PinIcon from '../../../../../assets/svgs/Pin'
import PinLightIcon from '../../../../../assets/svgs/PinLight'
import RingIcon from '../../../../../assets/svgs/Ring'

import useWindowWidth from '../../../../../hooks/useWindowWidth'

import { fetchCompany } from '../../api'
import { brandAlpha, monogramColors, readableOn, shade } from '../../format'

import AddToChatBlock from './components/AddToChatBlock'
import EventsBlock from './components/EventsBlock'
import Narrative from './components/Narrative'
import NewsBlock from './components/NewsBlock'
import PositionsBlock from './components/PositionsBlock'
import StatsBlock from './components/StatsBlock'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'

import style from './style.module.css'
import './style.css'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const SCORE_HINT =
  'Activity score out of 10 — how much this company has been hiring, publishing and running events lately.'

/**
 * The server picks the icon per insight; we only own the mapping to an asset.
 * These components ignore every prop, so sizing and recolouring is done with
 * descendant selectors in style.module.css.
 */
const INSIGHT_ICONS = {
  pen: PenIcon,
  bag: Bag2Icon,
  pin: PinIcon,
  remote: Location2Icon,
  people: PeopleIcon,
  chart: ChartsIcon,
  ring: RingIcon,
  hat: HatIcon,
  notes: NotesIcon,
}

/** Seven chips already wrap to three rows in the hero; six is the useful ceiling. */
const MAX_INSIGHTS = 6

/**
 * The company detail modal.
 *
 * The header renders straight from the list row we were handed, so opening a
 * card is instant; only the three list blocks wait on the detail request. The
 * detail response also carries a `company` object, but we deliberately keep
 * rendering the prop: the parent owns the optimistic `saved` flag, and swapping
 * the header to server data mid-flight would make the bookmark flicker back.
 */
const CompanyModal = ({ company, onClose, onToggleSave, onAuthError }) => {
  const isMobile = useWindowWidth() <= 768

  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)

  const dialogRef = useRef(null)
  // Guards against a text selection that starts inside the dialog and ends on
  // the backdrop being treated as a "click outside".
  const pressedBackdrop = useRef(false)
  // Held in a ref so a parent that re-creates the callback cannot re-trigger the
  // detail request; the effect only ever depends on the company and the retry.
  const authErrorRef = useRef(onAuthError)
  authErrorRef.current = onAuthError

  const orgKey = company?.orgKey

  // ---- detail ------------------------------------------------------------
  useEffect(() => {
    if (!orgKey) return undefined

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    fetchCompany({ orgKey, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return
        setDetail(data)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return

        // A 401 or 403 is not a per-block failure: the parent redirects to login
        // or swaps the whole page for the gate. Showing "could not load events"
        // three times over would just be noise on the way out, so the blocks are
        // left in their loading state and the handler takes it from here.
        const handler = authErrorRef.current
        if (handler && handler(err)) return

        setDetail(null)
        setError(err)
        setLoading(false)
      })

    return () => controller.abort()
  }, [orgKey, reloadToken])

  const handleRetry = useCallback(() => setReloadToken((n) => n + 1), [])

  // ---- dialog semantics --------------------------------------------------
  // Move focus into the dialog on open and hand it back on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement
    dialogRef.current?.focus()

    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [])

  // Escape closes; Tab is trapped inside the dialog.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }

      if (event.key !== 'Tab') return

      const node = dialogRef.current
      if (!node) return

      const focusable = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      )
      if (!focusable.length) {
        event.preventDefault()
        node.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === node || !node.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleBackdropMouseDown = (event) => {
    pressedBackdrop.current = event.target === event.currentTarget
  }

  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return
    if (!pressedBackdrop.current) return
    pressedBackdrop.current = false
    // Never forward the DOM event: the parent treats its argument as data.
    onClose()
  }

  if (!company) return null

  // ---- header data -------------------------------------------------------
  const colors = monogramColors(company.orgKey)
  const initials =
    company.initials || String(company.name || '?').trim().slice(0, 2).toUpperCase()
  const niche = company.industry || company.specialism || null
  const tags = Array.isArray(company.tags) ? company.tags : []
  const insights = (Array.isArray(company.insights) ? company.insights : [])
    .filter((insight) => insight && insight.label)
    .slice(0, MAX_INSIGHTS)
  // Number(null) is 0, and 0 is finite — a null score would render as "0.0/10".
  const hasScore = company.score !== null && company.score !== undefined
  const score = hasScore ? Number(company.score) : null

  const positions = detail?.positions || []
  const news = detail?.news || []
  const events = detail?.events || []
  const courses = detail?.courses || []
  // The employer's other Maps locations. `sources` (inferred content hostnames)
  // is gone: the registry now gives us a real website on company.website.
  const locations = detail?.locations || []

  // The company-authored half. Kept as its own object rather than merged into
  // `company`, so the two provenances can never be rendered as one.
  const narrative = detail?.narrative || null

  // A claimed booth dresses itself in the company's own colour. Falls back to
  // the stock backdrop when the booth is unclaimed or no colour was set.
  const brand = narrative?.profile?.brandColor || null
  const brandInk = brand ? readableOn(brand) : null
  const boothTagline = narrative?.profile?.tagline || null

  const blockProps = { loading, error, onRetry: handleRetry }

  const eventsBlock = <EventsBlock company={company} events={events} {...blockProps} />
  const newsBlock = <NewsBlock company={company} news={news} {...blockProps} />
  const positionsBlock = (
    <PositionsBlock company={company} positions={positions} courses={courses} {...blockProps} />
  )

  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
    <div
      className={style.companyModal}
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div className={style.companyModal_background} aria-hidden="true" />

      <div
        className={style.companyModal_content}
        role="dialog"
        aria-modal="true"
        aria-label={company.name}
        tabIndex={-1}
        ref={dialogRef}
      >
        {/* A zero-height sticky rail rather than a button absolutely positioned
            inside the hero: the modal content is what scrolls on a short
            viewport, so anchoring the close button to the header would carry it
            off the top of the screen the moment the user scrolled down. */}
        <div className={style.companyModal_closeRail}>
          <button
            type="button"
            className={style.companyModal_close}
            onClick={() => onClose()}
            aria-label="Close"
            data-log-action="spotlight_close_company"
          >
            <CrossSpotlightIcon />
          </button>
        </div>

        {/*
          A claimed booth is dressed in the company's own brand colour and reads
          as their stand; an unclaimed one keeps the stock backdrop. That is the
          point of a fair — every booth looking identical is the one thing a fair
          is not — and it is also the pitch: this is what claiming does.
        */}
        <div
          className={
            brand
              ? `${style.companyModal_header} ${style.companyModal_header_branded}`
              : style.companyModal_header
          }
          style={
            brand
              ? {
                  background: `linear-gradient(135deg, ${brand} 0%, ${shade(brand, 0.4)} 100%)`,
                  color: brandInk,
                }
              : { backgroundImage: `url(${CompanyBackground})` }
          }
        >
          {!brand && <div className={style.companyModal_backgroundCover} aria-hidden="true" />}

          {brand && (
            <span className={style.companyModal_boothRail} aria-hidden="true" style={{ background: brandAlpha(brandInk, 0.22) }} />
          )}

          {hasScore && (
            <div className={style.companyModal_rank} title={SCORE_HINT}>
              <span>
                {score.toFixed(1)}
                <span className={style.companyModal_rank_max}>/10</span>
              </span>
              <InfoLight />
            </div>
          )}

          <div className={style.companyModal_mainInfo}>
            {/* The booth sign. On a claimed booth it is the company's monogram
                on their own colour, sized like a stand banner rather than a
                logo slot. */}
            <div
              className={
                brand
                  ? `${style.companyModal_logo} ${style.companyModal_logo_branded}`
                  : style.companyModal_logo
              }
              style={
                brand
                  ? {
                      backgroundColor: brandAlpha(brandInk, 0.14),
                      boxShadow: `inset 0 0 0 1.5px ${brandAlpha(brandInk, 0.3)}`,
                      color: brandInk,
                    }
                  : {
                      backgroundColor: colors.background,
                      // The pastel accent reads as a ring; using it for the
                      // letters themselves would be ~1.4:1 against this
                      // background.
                      boxShadow: `inset 0 0 0 1.5px ${colors.foreground}`,
                    }
              }
            >
              <span
                className={style.companyModal_logo_initials}
                style={{ color: brand ? brandInk : colors.ink }}
                aria-hidden="true"
              >
                {initials}
              </span>
              <span className={style.companyModal_logo_name}>{company.name}</span>
            </div>

            {(niche || company.location) && (
              <div className={style.companyModal_mainIfo_content}>
                {niche && (
                  <span className={style.companyModal_mainIfo_content_niche}>{niche}</span>
                )}

                {company.location && (
                  <div className={style.companyModal_mainIfo_content_location}>
                    <PinLightIcon />
                    <span>
                      {company.location}
                      {/* Spelling out the spread here is what stops the modal
                          looking inconsistent with itself: a company summarised
                          as "Global" will have roles and events in countries the
                          one-line summary cannot name. */}
                      {company.locationDetail && (
                        <span className={style.companyModal_mainIfo_content_locationDetail}>
                          {' · '}
                          {company.locationDetail}
                        </span>
                      )}

                      {/* A booth is one Maps location, so an employer with
                          several is showing you one of them. Naming the others
                          stops the counts here reading as the whole company. */}
                      {locations.length > 0 && (
                        <span
                          className={style.companyModal_mainIfo_content_locationDetail}
                          title={locations
                            .map((l) => l.city || l.name)
                            .filter(Boolean)
                            .join(', ')}
                        >
                          {' · '}
                          {locations.length === 1
                            ? '1 other location'
                            : `${locations.length} other locations`}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* On a claimed booth the company's own one-liner goes on the
                banner, where a stand would print it. It is the first thing a
                person reads and the only line the company fully controls. */}
            {brand && boothTagline && (
              <p className={style.companyModal_boothTagline}>{boothTagline}</p>
            )}
          </div>

          {tags.length > 0 && (
            <ul className={style.companyModal_navigation}>
              {tags.map((tag) => (
                <li className={style.companyModal_navigation_item} key={tag}>
                  <span>{tag}</span>
                </li>
              ))}
            </ul>
          )}

          {/* The career-insight strip. Filled glass on the hero scrim rather
              than outlined like the tag pills: it is the answer to "should I
              care about this company", so it should read first, and the light
              chip also lets the block icons keep their navy without a recolour. */}
          {insights.length > 0 && (
            <ul className={style.companyModal_insights}>
              {insights.map((insight) => {
                const Icon = INSIGHT_ICONS[insight.icon]

                return (
                  <li className={style.companyModal_insights_item} key={insight.key}>
                    {Icon && (
                      <span
                        className={
                          insight.icon === 'hat'
                            ? `${style.companyModal_insights_icon} ${style.companyModal_insights_icon_hat}`
                            : style.companyModal_insights_icon
                        }
                        aria-hidden="true"
                      >
                        <Icon />
                      </span>
                    )}
                    <span className={style.companyModal_insights_label}>{insight.label}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* The company's own half, between the observed header and the observed
            dashboard. Placed before the grid deliberately: on a claimed booth
            the company's voice is the reason to keep reading, and on an
            unclaimed one this is the gap the company is being shown. */}
        <Narrative company={company} narrative={narrative} onAuthError={onAuthError} />

        <div className={style.companyModal_observed}>
          <span className={style.companyModal_observed_mark}>Observed by Chimp</span>
          <span className={style.companyModal_observed_rule} aria-hidden="true" />
        </div>

        <div className={style.companyModal_dashboard}>
          {isMobile ? (
            <Swiper
              spaceBetween={30}
              grabCursor={true}
              pagination={true}
              loop={true}
              modules={[Pagination]}
              className="companyModal_dashboard_swiper"
            >
              <SwiperSlide>{eventsBlock}</SwiperSlide>
              <SwiperSlide>{newsBlock}</SwiperSlide>
              <SwiperSlide>{positionsBlock}</SwiperSlide>
            </Swiper>
          ) : (
            <>
              {eventsBlock}

              {newsBlock}

              {positionsBlock}
            </>
          )}

          {/* Booth engagement where we have it — visitors, interactions, answer
              rate — rather than a repeat of the scraped counts already shown in
              the panels above. Falls back to those counts on an unclaimed booth,
              which has no engagement to report. */}
          <StatsBlock
            stats={
              narrative && narrative.boothStats && narrative.boothStats.length
                ? narrative.boothStats
                : company.stats
            }
          />

          <AddToChatBlock company={company} onToggleSave={onToggleSave} />
        </div>
      </div>
    </div>
  )
}

export default CompanyModal
